<template>
  <v-row no-gutters class="pt-5 fill-height">
    <v-col class="pr-2 fill-height" v-if="isFetching">
      <main-loader></main-loader>
    </v-col>
    <v-col class="pr-5" v-else>
      <card-title-row translation-path="sidebar.modelingPatterns">
        <template #buttons>
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="$i18n.t('button.addPattern')"
            @click="addPattern"
          />
        </template>
      </card-title-row>
      <sidebar-row border-bottom>
        <modeling-patterns-list
          :patterns="patterns"
          @editPattern="openPatternDialog"
          @deletePattern="handlePatternDelete"
          class="pb-5 px-0"
        ></modeling-patterns-list>
      </sidebar-row>

      <card-title-row translation-path="sidebar.receivers" class="py-4">
        <template #buttons>
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="$i18n.t('button.addReceiver')"
            @click="openReceiverPatternDialog"
          />
        </template>
      </card-title-row>
      <sidebar-row>
        <receivers-list
          :receivers="receiverPatterns"
          @editReceiver="openEditReceiverPatternDialog"
          @deleteReceiver="openDeleteDialog"
          class="px-0 pt-0 pb-4"
        />
      </sidebar-row>

      <main-input-dialog
        v-if="isReceiverDialogLoaded"
        :is-visible.sync="isReceiverDialogVisible"
        :inputs="receiverInputs"
        agree-text="dialog.title.add"
        dialog-width="40%"
        :title-translation="receiverDialogName"
        @modalClosed="onClose"
        :loading="isConfirmButtonLoading"
        @agree="computedAgreeAction"
      />
      <main-dialog
        v-if="isDeleteDialogLoaded"
        :is-visible.sync="isDeleteDialogVisible"
        :title="$i18n.t('dialog.title.deleteReceiver')"
        :body="$i18n.t('dialog.body.deleteRecipientConfirmation')"
        @agree="handleRecipientDelete"
        :loading="isConfirmButtonLoading"
        agree-text="dialog.agreeDelete"
        @modalClosed="onClose"
      />
      <modeling-pattern-dialog
        v-if="isPatternDialogLoaded"
        :is-visible.sync="isPatternDialogVisible"
        :pattern="editingPattern"
        @modalClosed="resetData"
      >
      </modeling-pattern-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import BaseRoundedButton from '@/components/BaseRoundedButton';
import ModelingPatternsList from '@/components/ModelingPatternsList';
import ReceiversList from '@/components/ReceiversList';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'ModelingPatterns',
  components: {
    SidebarRow,
    MainLoader,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    ReceiversList,
    ModelingPatternDialog: () => import('@/components/ModelingPatternDialog'),
    ModelingPatternsList,
    BaseRoundedButton,
    CardTitleRow,
  },
  data: () => ({
    isPatternDialogLoaded: false,
    isPatternDialogVisible: false,
    isReceiverDialogVisible: false,
    isReceiverDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isConfirmButtonLoading: false,
    editingPattern: undefined,
    editingReceiverPattern: undefined,
    isFetching: false,
  }),
  computed: {
    patterns: get('epanet/patterns'),
    receiverPatterns: get('epanet/recipientPatterns'),
    recipients: get('epanet/recipients'),

    computedAgreeAction() {
      return this.editingReceiverPattern === undefined ? this.handleRecipientAdd : this.handleRecipientEdit;
    },
    receiverDialogName() {
      return this.editingReceiverPattern === undefined
        ? this.$i18n.t('dialog.title.addReceiver')
        : this.$i18n.t('dialog.title.editReceiver');
    },
    receiverInputs() {
      return [
        {
          name: 'receiverName',
          translationPath: 'dialog.receiverName',
          clearable: true,
          dataType: {
            name: 'select',
          },
          cols: '10',
          rules: [v => !!v || v === undefined || 'rules.required'],
          items: this.recipients,
          value: this.editingReceiverPattern?.recipient,
        },
        {
          name: 'patternName',
          translationPath: 'dialog.patternName',
          clearable: true,
          dataType: {
            name: 'select',
          },
          cols: '10',
          rules: [v => !!v || v === undefined || 'rules.required'],
          items: this.patterns,
          'item-value': 'id',
          'item-text': 'name',
          value: this.getPatternIdByName(this.editingReceiverPattern?.pattern_name),
        },
      ];
    },
  },
  methods: {
    getPatterns: call('epanet/getPatterns'),
    getRecipients: call('epanet/getRecipients'),
    getRecipientPatterns: call('epanet/getRecipientPatterns'),
    getRecipientPattern: call('epanet/getRecipientPattern'),
    deleteRecipientPattern: call('epanet/deleteRecipientPattern'),
    editRecipientPattern: call('epanet/editRecipientPattern'),
    addRecipientPattern: call('epanet/addRecipientPattern'),
    deletePattern: call('epanet/deletePattern'),

    addPattern() {
      this.isPatternDialogLoaded = true;
      this.isPatternDialogVisible = true;
      this.editingPattern = {
        name: '',
        description: '',
        curve_type: '',
        data: [],
      };
    },
    openPatternDialog(c) {
      this.isPatternDialogLoaded = true;
      this.isPatternDialogVisible = true;
      this.editingPattern = c;
    },
    onClose() {
      this.receiverInputs[0].value = '';
      this.receiverInputs[1].value = '';
      this.editingReceiverPattern = undefined;
      this.isConfirmButtonLoading = false;
    },
    resetData() {
      this.editingPattern = undefined;
      this.isPatternDialogLoaded = false;
      this.isPatternDialogVisible = false;
    },
    openReceiverPatternDialog() {
      this.isReceiverDialogLoaded = true;
      this.isReceiverDialogVisible = true;
    },

    async openDeleteDialog(recipient_id) {
      this.editingReceiverPattern = await this.getRecipientPattern(recipient_id);
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    getPatternIdByName(name) {
      return this.patterns.find(p => p.name === name)?.id;
    },
    async handleRecipientDelete() {
      this.isConfirmButtonLoading = true;
      await this.deleteRecipientPattern(this.editingReceiverPattern.id);
      this.isDeleteDialogVisible = false;
      this.onClose();
    },
    async handleRecipientAdd() {
      this.isConfirmButtonLoading = true;
      const payload = {
        recipient: this.receiverInputs[0].value,
        pattern: this.receiverInputs[1].value,
      };
      try {
        await this.addRecipientPattern(payload);
        this.isReceiverDialogVisible = false;
        this.onClose();
      } catch (error) {
        this.isReceiverDialogVisible = false;
        this.onClose();
      }
    },
    async handleRecipientEdit() {
      this.isConfirmButtonLoading = true;
      const body = {
        recipient: this.receiverInputs[0].value,
        pattern: this.receiverInputs[1].value,
      };
      const payload = {
        recipient_id: this.editingReceiverPattern.id,
        body,
      };
      try {
        await this.editRecipientPattern(payload);
        this.isReceiverDialogVisible = false;
        this.onClose();
      } catch (error) {
        this.isReceiverDialogVisible = false;
        this.onClose();
      }
    },
    async openEditReceiverPatternDialog(recipient_id) {
      this.editingReceiverPattern = await this.getRecipientPattern(recipient_id);
      this.isReceiverDialogLoaded = true;
      this.isReceiverDialogVisible = true;
    },
    async handlePatternDelete(pattern_id) {
      await this.deletePattern(pattern_id);
    },
    async init() {
      this.isFetching = true;
      await Promise.all([this.getPatterns(), this.getRecipientPatterns(), this.getRecipients()]);
      this.isFetching = false;
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.borderTop {
  @include border(top);
}
</style>
